import React from 'react'
import PropTypes from 'prop-types'
import { Column } from '@sainsburys-tech/bolt'
import { defaultImgParams, onLinkClickFn } from '../../../helpers/ComponentHelper'
import { agnostifyUrl } from '../../../helpers/utils'
import * as Styled from './M029.styles'
import { createGolContentDataAttributes } from '../../../helpers/tracking'
import Image from '../../../helpers/Image/Image'

/**
 * M029 is an editorial component with image and text in a single full-width column, on top of a full-bleed grey overlay.
 */
const M029 = ({
  heading,
  backgroundImageUrl,
  bodyCopy,
  ctaCopy,
  imageFormat,
  linkTarget,
  linkTitle,
  linkUrl,
  textPosition,
  onLinkClick,
  consumingGridType,
  positionInGrid
}) => {
  const getGOLDataAttributes = (action) =>
    createGolContentDataAttributes(
      consumingGridType,
      `M029 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      linkTitle || '',
      linkUrl,
      heading,
      action
    )

  const addGolTracking = (evt) => {
    const dataAttributes = getGOLDataAttributes()
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (evt) => {
    if (onLinkClick) {
      onLinkClickFn(evt, onLinkClick, linkUrl)
    }
    addGolTracking(evt)
  }

  const backgroundImageFn = () => (
    <Image imageFormat={imageFormat} src={`${backgroundImageUrl}${defaultImgParams}&w=1170`} smart alt='' />
  )

  return (
    <Styled.Container data-test='m029-container' gutterValue={0} row='xs' block='sm' {...getGOLDataAttributes('Load')}>
      {backgroundImageUrl && (
        <Styled.ImageWrapper data-test='m029-image-wrapper' block='xs'>
          {linkUrl ? (
            <a
              data-test='m029-image-link'
              href={agnostifyUrl(linkUrl)}
              title={linkTitle}
              onClick={(evt) => ctaClick(evt)}
              target={linkTarget}
            >
              {backgroundImageFn()}
            </a>
          ) : (
            backgroundImageFn()
          )}
        </Styled.ImageWrapper>
      )}
      {textPosition === 'right' && <Column xs={12} sm={7} />}
      <Styled.TextWrapper data-test='m029-text-wrapper' gutterValue={0} smGutterValue={1} sm={5}>
        <h4 data-test='m029-heading'>{heading}</h4>
        <p data-test='m029-bodyCopy'>{bodyCopy}</p>
        {linkUrl && linkTitle && ctaCopy && (
          <p>
            <a
              data-test='m029-ctaCopy'
              href={agnostifyUrl(linkUrl)}
              title={linkTitle}
              onClick={(evt) => ctaClick(evt)}
              target={linkTarget}
            >
              {ctaCopy}
            </a>
          </p>
        )}
      </Styled.TextWrapper>
      {textPosition === 'left' && <Column xs={12} sm={7} />}
    </Styled.Container>
  )
}

M029.propTypes = {
  /** Required background image url */
  backgroundImageUrl: PropTypes.string.isRequired,
  /** Required body copy text */
  bodyCopy: PropTypes.string.isRequired,
  /** Optional text for the CTA */
  ctaCopy: PropTypes.string,
  /** Required text for heading */
  heading: PropTypes.string.isRequired,
  /** Optional image format, eg. avif */
  imageFormat: PropTypes.string,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional link title, can be seen when hovering over the CTA */
  linkTitle: PropTypes.string,
  /** Optional link url */
  linkUrl: PropTypes.string,
  /** Required text positon, default to left */
  textPosition: PropTypes.oneOf(['right', 'center', 'left']).isRequired,
  /** Consuming grid type  */
  consumingGridType: PropTypes.string,
  /** Optional position number populated when the component is used within a grid */
  positionInGrid: PropTypes.number
}

M029.defaultProps = {
  ctaCopy: '',
  linkTarget: '',
  linkUrl: '',
  linkTitle: '',
  consumingGridType: 'isolated'
}

export default M029
